import api from './axiosInstance';

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeOperator = (raw) => ({
  key: String(raw?.key || '').trim(),
  label: String(raw?.label || raw?.key || '').trim(),
  requiresValue: Boolean(raw?.requiresValue),
  requiresSecondValue: Boolean(raw?.requiresSecondValue),
  supportsMultipleValues: Boolean(raw?.supportsMultipleValues),
});

const normalizeField = (field, entity) => ({
  fieldKey: String(field?.key || '').trim(),
  label: String(field?.label || field?.key || '').trim(),
  entity: String(entity?.label || entity?.key || 'General').trim(),
  entityKey: String(entity?.key || 'General').trim(),
  dataType: String(field?.dataType || 'text').toLowerCase(),
  selectable: Boolean(field?.selectable),
  filterable: Boolean(field?.filterable),
  groupable: Boolean(field?.groupable),
  sortable: Boolean(field?.sortable),
  supportsValueLookup: Boolean(field?.supportsValueLookup),
  supportedAggregates: normalizeArray(field?.supportedAggregates).map((item) =>
    String(item).toLowerCase()
  ),
  supportedOperators: normalizeArray(field?.supportedOperators).map((item) =>
    String(item).toLowerCase()
  ),
});

const normalizeErrorMessage = (error) => {
  const responseData = error?.response?.data;
  const status = error?.response?.status;
  const message =
    responseData?.Message ||
    responseData?.message ||
    error?.customMessage ||
    error?.message ||
    'Error inesperado.';
  const detail = responseData?.Detail || responseData?.detail;

  if (status === 503 && detail) {
    return `${message} ${detail}`;
  }

  return message;
};

export const reportsBuilderApi = {
  async getCatalog() {
    try {
      const response = await api.get('/ReportBuilder/catalog');
      const payload = response.data || {};
      const entities = normalizeArray(payload.entities);
      const operators = normalizeArray(payload.operators)
        .map(normalizeOperator)
        .filter((item) => item.key);

      const fields = entities
        .flatMap((entity) =>
          normalizeArray(entity.fields).map((field) => normalizeField(field, entity))
        )
        .filter((field) => field.fieldKey);

      return {
        entities: entities.map((entity) => ({
          key: String(entity?.key || ''),
          label: String(entity?.label || entity?.key || ''),
          description: String(entity?.description || ''),
        })),
        fields,
        operators,
      };
    } catch (error) {
      error.customMessage = normalizeErrorMessage(error);
      throw error;
    }
  },

  async getFieldValues(fieldKey, search = '', limit = 50) {
    try {
      const response = await api.get('/ReportBuilder/catalog/values', {
        params: { fieldKey, search, limit },
      });
      const payload = response.data;
      const rows = Array.isArray(payload?.values)
        ? payload.values
        : Array.isArray(payload)
          ? payload
          : [];

      return rows.map((item) => ({
        value: String(item?.value ?? item?.id ?? item?.key ?? ''),
        label: String(
          item?.label ?? item?.name ?? item?.value ?? item?.id ?? item?.key ?? ''
        ),
      }));
    } catch (error) {
      error.customMessage = normalizeErrorMessage(error);
      throw error;
    }
  },

  async query(definition, signal) {
    try {
      const response = await api.post('/ReportBuilder/query', definition, { signal });
      const payload = response.data || {};
      return {
        columns: Array.isArray(payload.columns) ? payload.columns : [],
        data: Array.isArray(payload.data) ? payload.data : [],
        rowCount: Number(payload.rowCount || 0),
        recordsFound: Number(payload.recordsFound || 0),
        executionMs: Number(payload.executionMs || 0),
      };
    } catch (error) {
      if (error?.response?.status === 499) {
        error.isClientCanceled = true;
      }
      error.customMessage = normalizeErrorMessage(error);
      throw error;
    }
  },

  async saveReport(payload) {
    try {
      const response = await api.post('/ReportBuilder/reports', payload);
      return response.data || {};
    } catch (error) {
      error.customMessage = normalizeErrorMessage(error);
      throw error;
    }
  },

  async getReportById(reportId) {
    try {
      const response = await api.get(`/ReportBuilder/reports/${encodeURIComponent(reportId)}`);
      return response.data || {};
    } catch (error) {
      error.customMessage = normalizeErrorMessage(error);
      throw error;
    }
  },

  async listReports({ page = 1, pageSize = 20, search = '' } = {}) {
    try {
      const response = await api.get('/ReportBuilder/reports', {
        params: {
          page: Number(page) || 1,
          pageSize: Math.min(Number(pageSize) || 20, 100),
          search: String(search || '').trim() || undefined,
        },
      });
      return response.data || {};
    } catch (error) {
      error.customMessage = normalizeErrorMessage(error);
      throw error;
    }
  },
};

export default reportsBuilderApi;
